/**
 * Buddy's mark: the MoveBuddy mark cut from a matte pearl. See `pearlMark.module.css`.
 *
 * A component rather than a bare class, unlike `brandMark`, because the pearl is several masked
 * layers and every surface would otherwise repeat them. Decorative everywhere: the words beside it
 * name Buddy.
 */

import clsx from 'clsx';
import type { CSSProperties } from 'react';

import styles from './pearlMark.module.css';

/** Standing still, or Buddy working (the light breathes; the mark never moves). */
export type PearlMarkState = 'rest' | 'work';

/**
 * The hero figure's light (board P5e, variant D): whether the pearl is lit, where its bright side
 * points, and how many pulses and sweeps have been asked for. A new count plays one more.
 */
export interface PearlLight {
  readonly lit: boolean;
  /** The cone's start angle, in degrees. */
  readonly angle: number;
  readonly pulses: number;
  readonly sweeps: number;
}

interface PearlMarkProps {
  readonly state?: PearlMarkState;
  /** The hero figure's light; `null` for every other mark. */
  readonly light?: PearlLight | null;
  /** The surface's size class: `width` and `height`. */
  readonly className?: string;
}

export function PearlMark({ state = 'rest', light = null, className }: PearlMarkProps) {
  return (
    <span
      className={clsx(styles.pearl, state === 'work' && styles.work, className)}
      style={light === null ? undefined : ({ '--pearl-ang': `${String(light.angle)}deg` } as CSSProperties)}
      aria-hidden="true"
    >
      <span className={clsx(styles.layer, styles.ghost)} />
      <span className={clsx(styles.layer, styles.ink)} />
      {light !== null && (
        <>
          {/* Keyed by its count: each new pulse or sweep is a new layer, and plays once as it mounts. */}
          {light.pulses > 0 && <span key={`pulse-${String(light.pulses)}`} className={clsx(styles.layer, styles.pulse)} />}
          <span className={clsx(styles.layer, styles.lit, light.lit && styles.on)} />
        </>
      )}
      <span className={clsx(styles.layer, styles.highlight)} />
      {light !== null && light.sweeps > 0 && <span key={`sweep-${String(light.sweeps)}`} className={clsx(styles.layer, styles.sweep)} />}
    </span>
  );
}
